import { v4 } from 'uuid';
import { SysUIParameter, sendSysUI } from './common';
import { OfferItem } from '@wwe-portal/entities/offer.type';

interface InterstitialSurveyData {
  id: number;
  name: string;
  description: string;
  buttonURL: string;
  buttonName: string;
  pageName: string;
  cover: string;
  isActive: boolean;
  updateKey: string;
  toShow: boolean;
}

interface InterstitialOfferData {
  id: number;
  name: string;
  description: string;
  offerId: string;
  pageName: string;
  cover: string;
  isActive: boolean;
  suggestedOfferId: string;
  updateKey: string;
  startDate?: string;
  endDate?: string;
}

export const parseScreenContentEvent = (
  offerDetails: Pick<
    InterstitialOfferData,
    | 'name'
    | 'description'
    | 'cover'
    | 'offerId'
    | 'suggestedOfferId'
    | 'startDate'
    | 'endDate'
  >,
  transactionId?: string
) => {
  return {
    name: offerDetails.name || '',
    description: offerDetails.description || '',
    offer_id: offerDetails.offerId || '',
    suggested_offer_id: offerDetails.suggestedOfferId || '',
    transaction_id: transactionId ?? v4(),
    asset: offerDetails.cover,
    button_label: '',
    button_url: '',
    start_date: offerDetails.startDate,
    end_date: offerDetails.endDate,
  };
};

export const parseScreenContentEventV2 = (
  offerDetails: Pick<InterstitialOfferData, 'offerId'>,
  offerItems: OfferItem[]
) => {
  return {
    offer_id: offerDetails.offerId,
    featured_item: offerItems.map((item) => item.itemID),
  };
};

type InterstitialOfferDataForAnalytics = Pick<
  InterstitialOfferData,
  | 'name'
  | 'description'
  | 'cover'
  | 'offerId'
  | 'suggestedOfferId'
  | 'startDate'
  | 'endDate'
>;

// 1. Offer interstitial is loaded
export const sendUIInterstitialLoaded = (
  offerDetails: InterstitialOfferDataForAnalytics,
  offerItems: OfferItem[]
) => {
  const finalPayload: SysUIParameter = {
    screen_content: JSON.stringify(
      parseScreenContentEventV2(offerDetails, offerItems)
    ),
    interaction_object: null,
    flow_name: 'web_offer_pop_up',
    interaction_type: 'load',
    screen_subcategory: 'offer',
    screen_type: 'pop_up',
    interaction_object_category: null,
    flow_order: null,
    screen_name: offerDetails.name,
  };

  sendSysUI(finalPayload);
};

// 1. Upsel is loaded
export const sendUIUpsellLoaded = (
  offerDetails: InterstitialOfferDataForAnalytics
) => {
  const finalPayload: SysUIParameter = {
    screen_content: parseScreenContentEvent(offerDetails),
    interaction_object: 'upsell_button',
    flow_name: 'web_interstitials',
    interaction_type: 'load',
    screen_subcategory: 'upsell',
    screen_type: 'pop_up',
    interaction_object_category: null,
    flow_order: null,
    screen_name: offerDetails.name,
  };

  sendSysUI(finalPayload);
};

export const sendUpsellOfferButtonTap = (
  offerDetails: InterstitialOfferDataForAnalytics
) => {
  const finalPayload: SysUIParameter = {
    screen_content: parseScreenContentEvent(offerDetails),
    interaction_object: 'upsell_button',
    flow_name: 'web_interstitials',
    interaction_type: 'tap',
    screen_subcategory: 'upsell',
    screen_type: 'pop_up',
    interaction_object_category: null,
    flow_order: null,
    screen_name: offerDetails.name,
  };

  sendSysUI(finalPayload);
};

// 2. Offer button is tapped (transaction involved)
export const sendInterstitialOfferButtonTap = (
  offerDetails: InterstitialOfferDataForAnalytics,
  offerItems: OfferItem[]
) => {
  const finalPayload: SysUIParameter = {
    screen_content: JSON.stringify(
      parseScreenContentEventV2(offerDetails, offerItems)
    ),
    interaction_object: 'offer_button',
    flow_name: 'web_offer_pop_up',
    interaction_type: 'tap',
    screen_subcategory: 'offer',
    screen_type: 'pop_up',
    interaction_object_category: null,
    flow_order: null,
    screen_name: offerDetails.name,
  };

  sendSysUI(finalPayload);
};

// 2. Offer button is tapped (transaction involved)
export const sendInterstitialCloseButtonTap = (
  offerDetails: InterstitialOfferDataForAnalytics,
  offerItems: OfferItem[]
) => {
  const finalPayload: SysUIParameter = {
    screen_content: JSON.stringify(
      parseScreenContentEventV2(offerDetails, offerItems)
    ),
    flow_name: 'web_offer_pop_up',
    interaction_object: 'close_button',
    interaction_type: 'tap',
    screen_subcategory: 'offer',
    screen_type: 'pop_up',
    interaction_object_category: null,
    flow_order: null,
    screen_name: offerDetails.name,
  };

  sendSysUI(finalPayload);
};

const parseSurveyToScreenContent = (surveyDetails: InterstitialSurveyData) => {
  return JSON.stringify({
    name: surveyDetails.name || '',
    description: surveyDetails.description || '',
    offer_id: '',
    suggested_offer_id: '',
    transaction_id: '',
    asset: surveyDetails.cover,
    button_label: surveyDetails.buttonName,
    button_url: surveyDetails.buttonURL,
    start_date: '',
    end_date: '',
  });
};

// 5. Survey interstitial is loaded
export const sendInterstitalSurveyLoaded = (
  surveyDetails: InterstitialSurveyData
) => {
  const finalPayload: SysUIParameter = {
    interaction_object: null,
    flow_name: 'web_interstitials',
    interaction_type: 'load',
    screen_subcategory: 'survey',
    screen_type: 'pop_up',
    screen_content: parseSurveyToScreenContent(surveyDetails),
    interaction_object_category: null,
    flow_order: null,
    screen_name: surveyDetails.name,
  };

  sendSysUI(finalPayload);
};

// 6. Survey button is tapped (redirected)
export const sendInterstitialSurveyButtonTap = (
  surveyDetails: InterstitialSurveyData
) => {
  const finalPayload: SysUIParameter = {
    flow_name: 'web_interstitials',
    interaction_type: 'tap',
    interaction_object: 'survey_button',
    screen_subcategory: 'survey',
    screen_type: 'pop_up',
    screen_content: parseSurveyToScreenContent(surveyDetails),
    interaction_object_category: null,
    flow_order: null,
    screen_name: surveyDetails.name,
  };

  sendSysUI(finalPayload);
};

// 6. Survey button is tapped (redirected)
export const sendInterstitialSurveyCloseButtonTap = (
  surveyDetails: InterstitialSurveyData
) => {
  const finalPayload: SysUIParameter = {
    flow_name: 'web_interstitials',
    interaction_type: 'tap',
    interaction_object: 'close_button',
    screen_subcategory: 'survey',
    screen_type: 'pop_up',
    screen_content: parseSurveyToScreenContent(surveyDetails),
    interaction_object_category: null,
    flow_order: null,
    screen_name: surveyDetails.name,
  };

  sendSysUI(finalPayload);
};
